import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { XMarkIcon, ArrowPathIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { QRCodeSVG } from 'qrcode.react';

const TwoFactorSetupModal = ({ isOpen, onClose, onSetupComplete, toastStyle, darkMode }) => {
  const [step, setStep] = useState('setup'); // setup, verify, complete
  const [qrCodeData, setQrCodeData] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);

  useEffect(() => {
    if (isOpen && step === 'setup') {
      generateQRCode();
    }
  }, [isOpen]);

  const generateQRCode = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/2fa/generate`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        setQrCodeData(response.data.qrCodeData);
        setSecret(response.data.secret);
      } else {
        setError('Failed to generate QR code');
      }
    } catch (error) {
      console.error('Error generating QR code:', error);
      setError('Failed to generate QR code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (index, value) => {
    if (isLoading) return;
    
    if (value.length <= 1 && /^[0-9]*$/.test(value)) {
      const newCode = [...verificationCode];
      newCode[index] = value;
      setVerificationCode(newCode);
      setError('');
      
      if (value !== '' && index < 5) {
        document.getElementById(`2fa-code-${index + 1}`).focus();
      } else if (index === 5 && value !== '') {
        handleVerifyCode(newCode);
      }
    }
  };

  const handleKeyDown = (index, event) => {
    if (isLoading) return;
    
    if (event.key === 'Backspace' && verificationCode[index] === '' && index > 0) {
      const newCode = [...verificationCode];
      newCode[index - 1] = '';
      setVerificationCode(newCode);
      document.getElementById(`2fa-code-${index - 1}`).focus();
    }
  };

  const handlePaste = (event) => {
    if (isLoading) return;
    
    event.preventDefault();
    const pastedData = event.clipboardData.getData('text/plain');
    
    const digits = pastedData.replace(/\D/g, '').slice(0, 6);
    
    if (digits.length > 0) {
      const newCode = [...verificationCode];
      
      for (let i = 0; i < 6; i++) {
        newCode[i] = digits[i] || '';
      }
      
      setVerificationCode(newCode);
      setError('');
      
      const nextEmptyIndex = newCode.findIndex(digit => digit === '');
      if (nextEmptyIndex !== -1) {
        document.getElementById(`2fa-code-${nextEmptyIndex}`).focus();
      } else {
        document.getElementById('2fa-code-5').focus();
        if (digits.length === 6) {
          handleVerifyCode(newCode);
        }
      }
    }
  };

  const handleVerifyCode = async (codeArray = verificationCode) => {
    if (isLoading) return;
    
    const code = codeArray.join('');
    
    if (!code || code.length !== 6 || codeArray.some(digit => digit === '')) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/2fa/verify-setup`,
        {
          code,
          secret
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setBackupCodes(response.data.backupCodes);
        setStep('complete');
      } else {
        setError(response.data.message || 'Invalid verification code');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setError(error.response?.data?.message || 'An error occurred during verification');
    } finally {
      setIsLoading(false);
    }
  };

  const handleComplete = () => {
    onSetupComplete();
    onClose();
    // Reset state for next time
    setStep('setup');
    setVerificationCode(['', '', '', '', '', '']);
    setError('');
    setQrCodeData('');
    setSecret('');
    setBackupCodes([]);
  };

  const getInputBorderColor = () => {
    if (error) return 'border-[#FF3B3B] dark:border-[#FF3B3B]';
    return 'border-[#727272] dark:border-[#444444]';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#181818] rounded-2xl border border-gray-200 dark:border-[#282828] max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-medium text-black dark:text-white">Set Up Two-Factor Authentication</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {step === 'setup' && (
            <div className="flex flex-col items-start gap-[25px] w-full">
              <div className="flex flex-col items-start gap-[13px] w-full">
                <h2 className="text-[24px] font-normal text-[#000000] dark:text-white leading-tight">
                  Scan this QR code with your<br />authenticator app
                </h2>
                <p className="text-[15px] text-[#515151] dark:text-[#666666]">
                  Use apps like Google Authenticator, Duo, or 1Password to scan this code
                </p>
              </div>

              <div className="flex flex-col items-center gap-[20px] w-full">
                {isLoading ? (
                  <div className="w-48 h-48 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
                    <ArrowPathIcon className="w-8 h-8 text-gray-400 animate-spin" />
                  </div>
                ) : qrCodeData ? (
                  <div className="p-4 bg-white rounded-lg">
                    <QRCodeSVG value={qrCodeData} size={192} />
                  </div>
                ) : (
                  <div className="w-48 h-48 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
                    <span className="text-gray-500 dark:text-gray-400 text-sm">Failed to load QR code</span>
                  </div>
                )}

                {secret && (
                  <div className="w-full p-3 bg-gray-50 dark:bg-[#282828] rounded-xl">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                      Can't scan? Enter this code manually:
                    </p>
                    <code className="text-sm font-mono text-gray-900 dark:text-white break-all">
                      {secret}
                    </code>
                  </div>
                )}
              </div>

              <button 
                onClick={() => setStep('verify')}
                disabled={isLoading || !qrCodeData}
                className={`w-full py-3 rounded-full text-[18px] font-normal ${
                  isLoading || !qrCodeData
                    ? 'bg-[#dfdfdf] dark:bg-[#444444] text-[#999999] dark:text-[#999999] cursor-not-allowed' 
                    : 'bg-[#000000] dark:bg-white text-white dark:text-black cursor-pointer'
                }`}
              >
                {isLoading ? 'Generating...' : 'I\'ve added it to my app'}
              </button>
            </div>
          )}

          {step === 'verify' && (
            <div className="flex flex-col items-start gap-[25px] w-full">
              <div className="flex flex-col items-start gap-[13px] w-full">
                <h2 className="text-[22px] md:text-[24px] font-normal text-[#000000] dark:text-white leading-tight">
                  Enter the 6-digit code from<br />your authenticator app
                </h2>
                <button 
                  className="text-[#000000] dark:text-white text-[15px] font-normal underline"
                  style={{ fontFamily: 'var(--chat-font-family)' }}
                  onClick={() => setStep('setup')}
                >
                  Need to scan the QR code again?
                </button>
                {error && (
                  <div className="flex items-center gap-2 text-[#FF3B3B] dark:text-[#FF3B3B] text-[15px] font-normal">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M8 5.3335V8.00016M8 10.6668H8.00667M14 8.00016C14 11.3137 11.3137 14.0002 8 14.0002C4.68629 14.0002 2 11.3137 2 8.00016C2 4.68645 4.68629 2.00016 8 2.00016C11.3137 2.00016 14 4.68645 14 8.00016Z" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>{error}</span>
                  </div>
                )}
              </div>

              <div className="flex flex-col items-start gap-[38px] w-full">
                <div className="flex gap-[2%] w-full justify-between">
                  {[0, 1, 2, 3, 4, 5].map((index) => (
                    <input
                      key={index}
                      id={`2fa-code-${index}`}
                      type="text"
                      maxLength="1"
                      value={verificationCode[index]}
                      onChange={(e) => handleInputChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={handlePaste}
                      style={{ fontFamily: 'var(--chat-font-family)' }}
                      className={`w-[15%] aspect-square bg-[#ffffff] dark:bg-[#282828] border-[1px] ${getInputBorderColor()} rounded-[16px] md:rounded-[23.10px] text-black dark:text-white text-[24px] md:text-[32px] font-normal text-center
                        focus:outline-none focus:ring-2 ${error ? 'focus:ring-[#FF3B3B]' : 'focus:ring-black'}`}
                    />
                  ))}
                </div>

                <button 
                  onClick={() => handleVerifyCode()}
                  disabled={isLoading || verificationCode.some(digit => digit === '')}
                  className={`w-full py-3 rounded-full text-[18px] font-normal ${
                    isLoading || verificationCode.some(digit => digit === '')
                      ? 'bg-[#dfdfdf] dark:bg-[#444444] text-[#999999] dark:text-[#999999] cursor-not-allowed' 
                      : 'bg-[#000000] dark:bg-white text-white dark:text-black cursor-pointer'
                  }`}
                >
                  {isLoading ? 'Verifying...' : 'Verify'}
                </button>
              </div>
            </div>
          )}

          {step === 'complete' && (
            <div className="flex flex-col items-start gap-[25px] w-full">
              <div className="flex flex-col items-start gap-[13px] w-full">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircleIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
                  <h2 className="text-[24px] font-normal text-[#000000] dark:text-white leading-tight">
                    2FA Setup Complete!
                  </h2>
                </div>
                <p className="text-[15px] text-[#515151] dark:text-[#666666]">
                  Your authenticator app is now set up. Save these backup codes in a safe place - you can use them to access your account if you lose your device.
                </p>
              </div>

              <div className="w-full p-4 bg-gray-50 dark:bg-[#282828] rounded-xl">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Backup Codes</h4>
                <div className="grid grid-cols-2 gap-2 text-sm font-mono">
                  {backupCodes.map((code, index) => (
                    <div key={index} className="text-gray-700 dark:text-gray-300">
                      {code}
                    </div>
                  ))}
                </div>
              </div>

              <button 
                onClick={handleComplete}
                className="w-full py-3 rounded-full text-[18px] font-normal bg-[#000000] dark:bg-white text-white dark:text-black cursor-pointer"
              >
                Continue
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TwoFactorSetupModal; 